/**
 * One walk over every user-visible string in a document.
 *
 * ⚠️ **IT WAS INSIDE `textGaps` AND IS OUT HERE BECAUSE A SECOND CONSUMER ARRIVED — AND THE SECOND
 * CONSUMER IS A SAFETY CHECK.** The publish-time scan (§5.12) reads a site's copy to decide whether
 * it is claiming to be somebody else. Writing a second walk for it would have been fifteen lines and
 * would have been wrong in the way this codebase has already paid for twice: two copies of one rule
 * that cannot disagree out loud (§0.5, and §7's lesson about `check:csp`).
 *
 * The specific failure it avoids is worth naming, because it is silent in both directions. Half a
 * page's copy lives in repeater ROWS — a footer's links, a `contact` block's opening hours, a
 * `features` list. A walk that forgot them would report a clean site and read none of the sentences
 * somebody would actually put a bank's name into; and the day a field type is added to the registry,
 * the copy adds it once rather than in two places, one of which nobody remembers. **An under-scan
 * looks exactly like a clean site**, which is the same shape as §5.19's "nothing is untagged" test
 * passing while a whole language scored zero.
 *
 * ⚠️ AND IT IS DRIVEN BY THE REGISTRY, NEVER BY `Object.values(props)`. A block's props hold things
 * that are not copy — a variant, a width, an image key, a link's `pageId` — and a walk that took
 * every string in the bag would hand the scanner a pile of ids. The registry already declares which
 * fields are text and what each is called; that declaration is the one the editor's forms, the
 * validator and the AI schemas all read (§0.5).
 */

import { getBlockDef } from './registry/index.ts'
import type { FieldSpec } from './prop-types.ts'
import type { BlockRef } from './ops.ts'
import { isLit, isText, resolve, type Lang, type Text } from './text.ts'
import type { Site } from './types.ts'

/**
 * Where a string lives, in the vocabulary the ops already speak.
 *
 * ⚠️ AN ADDRESS RATHER THAN A REFERENCE, so an entry collected from one document can be applied to a
 * later one. The editor is a stream of immutable documents — every keystroke makes a new site — and
 * a translation that arrives twenty seconds after it was asked for would otherwise be written into a
 * document that no longer exists.
 */
export type TextAt =
  | { where: 'site' }
  | { where: 'page'; pageId: string }
  | { where: 'prop'; ref: BlockRef; key: string; index?: number; subKey?: string }

export type TextEntry = {
  at: TextAt
  /** What the field is called on screen, in the language asked for. Context, not instruction. */
  label: string
  value: Text
}

/**
 * Every localised string in the document, in document order, with where it is and what it is called.
 *
 * ⚠️ DOCUMENT ORDER IS PART OF THE CONTRACT, not an accident of the loops. The scanner sends the copy
 * as one blob and a model reads a page that runs top to bottom very differently from a bag of
 * sentences; and the language filler's progress count has to be stable between two calls a few
 * seconds apart, or the number on screen goes up as often as it goes down.
 *
 * ⚠️ THE NAVBAR AND THE FOOTER COME FIRST AND ARE NOT PART OF A PAGE. They are the site's, rendered
 * on every page, and a walk that reached them through `site.pages` would visit each of them once per
 * page — which for the scan means paying for a footer nine times and for the gap list means offering
 * to translate one string nine times.
 */
export function eachText(site: Site, labelLang: Lang, visit: (entry: TextEntry) => void): void {
  const consider = (at: TextAt, label: string, value: unknown) => {
    if (!isText(value)) return
    visit({ at, label, value })
  }

  consider({ where: 'site' }, labelLang === 'ar' ? 'اسم الموقع' : 'Site name', site.title)

  for (const page of site.pages) {
    consider({ where: 'page', pageId: page.id }, labelLang === 'ar' ? 'اسم الصفحة' : 'Page name', page.title)
  }

  const blocks: { ref: BlockRef; type: string; props: Record<string, unknown> }[] = [
    { ref: { scope: 'navbar' }, type: site.navbar.type, props: site.navbar.props },
    { ref: { scope: 'footer' }, type: site.footer.type, props: site.footer.props },
  ]
  for (const page of site.pages) {
    for (const block of page.blocks) {
      blocks.push({ ref: { scope: 'page', pageId: page.id, blockId: block.id }, type: block.type, props: block.props })
    }
  }

  for (const block of blocks) {
    const def = getBlockDef(block.type)
    if (!def) continue
    for (const field of def.fields) {
      const label = resolve(field.label, labelLang)

      if (field.type.kind === 'list') {
        const rows = block.props[field.key]
        if (!Array.isArray(rows)) continue
        const item = field.type.item as FieldSpec[]
        rows.forEach((row, index) => {
          if (!row || typeof row !== 'object') return
          for (const sub of item) {
            consider(
              { where: 'prop', ref: block.ref, key: field.key, index, subKey: sub.key },
              // ⚠️ THE ROW'S LABEL IS THE LIST'S LABEL AND THE SUB-FIELD'S, JOINED. "Label" alone is
              // uselessly generic across a footer, a nav bar and a set of opening hours; "Links —
              // Label" is the thing on screen.
              `${label} — ${resolve(sub.label, labelLang)}`,
              (row as Record<string, unknown>)[sub.key],
            )
          }
        })
        continue
      }

      consider({ where: 'prop', ref: block.ref, key: field.key }, label, block.props[field.key])
    }
  }
}

/**
 * Everything the site says, as lines, for something that has to READ a site rather than edit it.
 *
 * ⚠️ **BOTH LANGUAGES, AND A `{ lit }` ONCE.** The scanner is looking for what a page claims to be,
 * and a site whose English is a florist and whose Arabic is a bank is precisely the case worth
 * catching — so filtering to one language would be building the blind spot deliberately. A `{ lit }`
 * is one string used in both (a phone number, a brand, an address), so it contributes one line.
 *
 * ⚠️ AND IT IS DEDUPLICATED AND TRIMMED, WHICH IS ABOUT COST RATHER THAN TIDINESS. A nav bar and a
 * footer repeat the same four link labels on a site with four pages; sending each occurrence is
 * paying for the same tokens several times to tell a model something it already read. Order is the
 * first occurrence's.
 */
export function siteCopy(site: Site): string[] {
  const seen = new Set<string>()
  const lines: string[] = []

  const add = (value: string | undefined) => {
    const text = value?.trim()
    if (!text || seen.has(text)) return
    seen.add(text)
    lines.push(text)
  }

  eachText(site, 'en', ({ value }) => {
    if (isLit(value)) {
      add(value.lit)
      return
    }
    add(value.en)
    add(value.ar)
  })

  return lines
}
